import { useState, useEffect } from "react";
import { MainLayout } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Wind,
  Brain,
  Apple,
  Heart,
  Pencil,
  Save,
  X,
  Trash2,
  Plus,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Eye,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

// Category configuration
const CATEGORIES = [
  { key: "respirazione", name: "Respirazione", icon: Wind, color: "bg-cyan-500" },
  { key: "meditazione", name: "Meditazione", icon: Brain, color: "bg-purple-500" },
  { key: "alimentazione", name: "Alimentazione", icon: Apple, color: "bg-green-500" },
  { key: "gestione_stress", name: "Gestione Stress", icon: Heart, color: "bg-teal-500" },
];

export default function AdminTecnichePage() {
  const [tecniche, setTecniche] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("respirazione");
  const [selectedTecnica, setSelectedTecnica] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [source, setSource] = useState("default");

  // Load all tecniche
  useEffect(() => {
    loadTecniche();
  }, []);

  const loadTecniche = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/tecniche-benessere`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTecniche(data.tecniche || {});
        setSource(data.source || "default");
      } else {
        toast.error("Errore nel caricamento delle tecniche");
      }
    } catch (error) {
      console.error("Error loading tecniche:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog for a tecnica
  const openEditDialog = (tecnica) => {
    setSelectedTecnica(tecnica);
    setEditForm({
      id: tecnica.id || "",
      titolo: tecnica.titolo || "",
      descrizione: tecnica.descrizione || "",
      spiegazione_scientifica: tecnica.spiegazione_scientifica || "",
      indicazioni: tecnica.indicazioni || "",
      benefici: tecnica.benefici || [],
      benefici_dettagliati: tecnica.benefici_dettagliati || {},
      immagini: tecnica.immagini || [],
    });
    setIsEditDialogOpen(true);
  };

  // Save tecnica
  const saveTecnica = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/tecniche-benessere/${selectedCategory}/tecnica/${editForm.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (response.ok) {
        toast.success("Tecnica salvata con successo!");
        setIsEditDialogOpen(false);
        loadTecniche();
      } else {
        toast.error("Errore nel salvataggio");
      }
    } catch (error) {
      console.error("Error saving tecnica:", error);
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  // Delete tecnica
  const deleteTecnica = async (tecnicaId) => {
    if (!confirm("Sei sicuro di voler eliminare questa tecnica?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/tecniche-benessere/${selectedCategory}/tecnica/${tecnicaId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success("Tecnica eliminata");
        loadTecniche();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Reset to defaults
  const resetToDefaults = async () => {
    if (!confirm("Sei sicuro di voler ripristinare tutte le tecniche ai valori di default? Tutte le modifiche andranno perse.")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/tecniche-benessere/reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Tecniche ripristinate ai valori di default");
        loadTecniche();
      } else {
        toast.error("Errore nel ripristino");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Add new image to tecnica
  const addImage = () => {
    setEditForm({
      ...editForm,
      immagini: [...(editForm.immagini || []), { url: "", alt: "", caption: "" }],
    });
  };

  // Remove image from tecnica
  const removeImage = (index) => {
    const newImages = [...editForm.immagini];
    newImages.splice(index, 1);
    setEditForm({ ...editForm, immagini: newImages });
  };

  // Update image
  const updateImage = (index, field, value) => {
    const newImages = [...editForm.immagini];
    newImages[index] = { ...newImages[index], [field]: value };
    setEditForm({ ...editForm, immagini: newImages });
  };

  // Add new benefit
  const addBenefit = () => {
    setEditForm({
      ...editForm,
      benefici: [...(editForm.benefici || []), ""],
    });
  };

  // Update benefit detail
  const updateBenefitDetail = (key, value) => {
    setEditForm({
      ...editForm,
      benefici_dettagliati: {
        ...editForm.benefici_dettagliati,
        [key]: value,
      },
    });
  };

  const currentCategoryData = tecniche[selectedCategory] || { tecniche: [] };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              Gestione Tecniche di Benessere
            </h1>
            <p className="text-slate-600 mt-1">
              Modifica testi, immagini e contenuti delle tecniche
            </p>
            {source === "database" && (
              <Badge className="mt-2 bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Dati personalizzati salvati
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadTecniche}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Ricarica
            </Button>
            <Button variant="outline" onClick={resetToDefaults} className="text-red-600 hover:text-red-700">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Reset Default
            </Button>
            <a href="/benessere/tecniche" target="_blank">
              <Button variant="outline">
                <Eye className="w-4 h-4 mr-2" />
                Anteprima
              </Button>
            </a>
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
          <TabsList className="grid grid-cols-4 mb-6">
            {CATEGORIES.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <TabsTrigger key={cat.key} value={cat.key} className="flex items-center gap-2">
                  <IconComponent className="w-4 h-4" />
                  {cat.name}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {CATEGORIES.map((cat) => (
            <TabsContent key={cat.key} value={cat.key}>
              {/* Category Info Card */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center`}>
                      <cat.icon className="w-4 h-4 text-white" />
                    </div>
                    {currentCategoryData.name || cat.name}
                  </CardTitle>
                  <CardDescription>
                    {currentCategoryData.description || "Descrizione categoria"}
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Tecniche List */}
              <div className="space-y-4">
                {(currentCategoryData.tecniche || []).map((tecnica, index) => (
                  <Card key={tecnica.id || index} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant="outline" className="text-xs">
                              #{index + 1}
                            </Badge>
                            <h3 className="font-semibold text-lg text-slate-800">
                              {tecnica.titolo}
                            </h3>
                          </div>
                          
                          <p className="text-slate-600 text-sm line-clamp-2 mb-3">
                            {tecnica.descrizione?.substring(0, 200)}...
                          </p>

                          {/* Images preview */}
                          {tecnica.immagini && tecnica.immagini.length > 0 && (
                            <div className="flex gap-2 mb-3">
                              {tecnica.immagini.slice(0, 3).map((img, imgIdx) => (
                                <img
                                  key={imgIdx}
                                  src={img.url}
                                  alt={img.alt}
                                  className="w-16 h-16 object-cover rounded-lg border"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ))}
                              {tecnica.immagini.length > 3 && (
                                <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500 text-sm">
                                  +{tecnica.immagini.length - 3}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Benefits badges */}
                          <div className="flex flex-wrap gap-1">
                            {(tecnica.benefici || []).slice(0, 4).map((beneficio, i) => (
                              <Badge key={i} variant="secondary" className="text-xs bg-green-50 text-green-700">
                                {beneficio}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(tecnica)}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Modifica
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => deleteTecnica(tecnica.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifica Tecnica</DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="content" className="mt-4">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="content">Contenuto</TabsTrigger>
                <TabsTrigger value="images">Immagini</TabsTrigger>
                <TabsTrigger value="benefits">Benefici</TabsTrigger>
              </TabsList>

              {/* Content Tab */}
              <TabsContent value="content" className="space-y-4 mt-4">
                <div>
                  <Label>Titolo</Label>
                  <Input
                    value={editForm.titolo || ""}
                    onChange={(e) => setEditForm({ ...editForm, titolo: e.target.value })}
                    placeholder="Titolo della tecnica"
                  />
                </div>

                <div>
                  <Label>Descrizione</Label>
                  <Textarea
                    value={editForm.descrizione || ""}
                    onChange={(e) => setEditForm({ ...editForm, descrizione: e.target.value })}
                    placeholder="Descrizione dettagliata..."
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Usa **testo** per grassetto. Supporta più paragrafi.
                  </p>
                </div>

                <div>
                  <Label>Spiegazione Scientifica</Label>
                  <Textarea
                    value={editForm.spiegazione_scientifica || ""}
                    onChange={(e) => setEditForm({ ...editForm, spiegazione_scientifica: e.target.value })}
                    placeholder="Spiegazione scientifica con studi e ricerche..."
                    rows={8}
                    className="font-mono text-sm"
                  />
                </div>

                <div>
                  <Label>Come Praticare (Guida Passo-Passo)</Label>
                  <Textarea
                    value={editForm.indicazioni || ""}
                    onChange={(e) => setEditForm({ ...editForm, indicazioni: e.target.value })}
                    placeholder="Guida dettagliata passo per passo..."
                    rows={10}
                    className="font-mono text-sm"
                  />
                </div>
              </TabsContent>

              {/* Images Tab */}
              <TabsContent value="images" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <Label>Immagini della Tecnica</Label>
                  <Button variant="outline" size="sm" onClick={addImage}>
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi Immagine
                  </Button>
                </div>

                {(editForm.immagini || []).map((img, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex gap-4">
                      {/* Image Preview */}
                      <div className="w-32 h-32 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {img.url ? (
                          <img
                            src={img.url}
                            alt={img.alt || "Preview"}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = ""; e.target.alt = "Errore"; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <ImageIcon className="w-8 h-8" />
                          </div>
                        )}
                      </div>

                      {/* Image Fields */}
                      <div className="flex-1 space-y-2">
                        <div>
                          <Label className="text-xs">URL Immagine</Label>
                          <Input
                            value={img.url || ""}
                            onChange={(e) => updateImage(index, "url", e.target.value)}
                            placeholder="https://..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Testo Alternativo (Alt)</Label>
                          <Input
                            value={img.alt || ""}
                            onChange={(e) => updateImage(index, "alt", e.target.value)}
                            placeholder="Descrizione per accessibilità"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Didascalia</Label>
                          <Input
                            value={img.caption || ""}
                            onChange={(e) => updateImage(index, "caption", e.target.value)}
                            placeholder="Didascalia sotto l'immagine"
                            className="text-sm"
                          />
                        </div>
                      </div>

                      {/* Remove Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => removeImage(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}

                {(!editForm.immagini || editForm.immagini.length === 0) && (
                  <div className="text-center py-8 text-slate-500">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nessuna immagine. Clicca "Aggiungi Immagine" per iniziare.</p>
                  </div>
                )}
              </TabsContent>

              {/* Benefits Tab */}
              <TabsContent value="benefits" className="space-y-4 mt-4">
                <div>
                  <Label>Lista Benefici (separati da virgola)</Label>
                  <Input
                    value={(editForm.benefici || []).join(", ")}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      benefici: e.target.value.split(",").map(b => b.trim()).filter(b => b)
                    })}
                    placeholder="Beneficio 1, Beneficio 2, Beneficio 3"
                  />
                </div>

                <div className="border-t pt-4 mt-4">
                  <Label className="text-lg font-semibold">Benefici Spiegati</Label>
                  <p className="text-sm text-slate-500 mb-4">
                    Aggiungi una spiegazione dettagliata per ogni beneficio
                  </p>

                  {(editForm.benefici || []).map((beneficio, index) => (
                    <div key={index} className="mb-4">
                      <Label className="text-sm font-medium text-green-700">{beneficio}</Label>
                      <Textarea
                        value={editForm.benefici_dettagliati?.[beneficio] || ""}
                        onChange={(e) => updateBenefitDetail(beneficio, e.target.value)}
                        placeholder={`Spiega come funziona "${beneficio}"...`}
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                <X className="w-4 h-4 mr-1" />
                Annulla
              </Button>
              <Button onClick={saveTecnica} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Salva Modifiche
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
